// components/subscription/PaymentComponent/index.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatCurrency } from '../../../utils/format';
import { subscriptionService } from '../../../services/subscription.service';
import { toast } from 'sonner';
import { Upload, Building2, Banknote } from 'lucide-react';
import { Button } from '../../../styles/components';
import {
    PaymentContainer,
    PaymentHeader,
    PaymentDetails,
    BankDetails,
    BankItem,
    UploadSection,
    DragDropArea,
    PreviewSection,
    ImagePreview,
} from './styles';

const PaymentComponent = ({ requestData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    // Handle drag events
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    // Validate and set file
    const handleFile = (file) => {
        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a JPEG, PNG or PDF file', {
				style: { padding: "1rem" },
			});
            return;
        }

        // Check file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size should not exceed 5MB', {
				style: { padding: "1rem" },
			});
            return;
        }

        setFile(file);
    };

    // Handle payment submission
    const handleSubmit = async () => {
        if (!file) {
            toast.error('Please upload payment proof', {
				style: { padding: "1rem" },
			});
            return;
        }

        try {
            setLoading(true);

            const paymentDetails = {
                amount: requestData.finalPrice,
                method: 'bank_transfer',
                date: new Date().toISOString(),
            };

            await subscriptionService.submitPayment(
                requestData.id,
                paymentDetails,
                file
            );

            toast.success('Payment proof submitted successfully', {
				style: { padding: "1rem" },
			});
            navigate('/dashboard');

        } catch (error) {
            toast.error(error.message || 'Failed to submit payment', {
				style: { padding: "1rem" },
			});
        } finally {
            setLoading(false);
        }
    };

    return (
        <PaymentContainer>
            <PaymentHeader>
                <h2>Complete Your Payment</h2>
                <p>Please transfer the amount and upload payment proof</p>
            </PaymentHeader>

            <PaymentDetails>
                <div className="amount-section">
                    <Banknote size={24} />
                    <div>
                        <h3>Payment Amount</h3>
                        <p className="amount">{formatCurrency(requestData.finalPrice)}</p>
                    </div>
                </div>

                <BankDetails>
                    <h3><Building2 size={20} /> Bank Transfer Details</h3>
                    <BankItem>
                        <span>Bank Name:</span>
                        <span>Nations Trust Bank</span>
                    </BankItem>
                    <BankItem>
                        <span>Account Name:</span>
                        <span>T K P Perera</span>
                    </BankItem>
                    <BankItem>
                        <span>Account Number:</span>
                        <span>2006000050799</span>
                    </BankItem>
                    <BankItem>
                        <span>Reference:</span>
                        <span>{requestData.id}</span>
                    </BankItem>
                </BankDetails>

                <UploadSection>
                    <h3><Upload size={20} /> Upload Payment Proof</h3>
                    <DragDropArea
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        dragActive={dragActive}
                    >
                        <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={handleFileSelect}
                            hidden
                            id="file-upload"
                        />
                        <label htmlFor="file-upload">
                            {file ? (
                                <span className="file-selected">{file.name}</span>
                            ) : (
                                <>
                                    <Upload size={24} />
                                    <span>Drag and drop or click to select file</span>
                                    <span className="file-info">JPG, PNG or PDF (max. 5MB)</span>
                                </>
                            )}
                        </label>
                    </DragDropArea>

                    {file && file.type.startsWith('image/') && (
                        <PreviewSection>
                            <h4>Preview</h4>
                            <ImagePreview>
                                <img src={URL.createObjectURL(file)} alt="Payment proof" />
                            </ImagePreview>
                        </PreviewSection>
                    )}
                </UploadSection>

                <div className="actions">
                    <Button
                        onClick={() => navigate('/dashboard')}
                        variant="outlined"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!file || loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Payment'}
                    </Button>
                </div>
            </PaymentDetails>
        </PaymentContainer>
    );
};

export default PaymentComponent;